import _ from 'lodash';
import { listStoreBuilder } from '@/main/store/pages/list/builder';
import { apiProvider as vozidloProvider } from '@/modules/kartoteka/vozidlo/api';
import { apiProvider as partnerProvider } from '@/modules/kartoteka/partner/api';
import { apiProvider as fakturacniProfilProvider } from '@/modules/ciselnik/zakaznicke/zakaznickaSkupina/fakturacniProfil/api';
import { apiProvider as nakladoveStrediskoProvider } from '@/modules/ciselnik/firemni/nakladoveStredisko/api';
import { DropdownItem } from '@/main/store/@types/dropdown.d';
import { convertToObjects, Row, Table } from '@/main/utils/table';
import { updateFilterAction } from '@/main/store/actionsFunctions';

import { listMutations, listActions } from './types';
import { NAME, CRUMB_LABEL_KEY } from '../constants';
import { provider as apiProvider } from '../api';

type FakturacniProfil = {
	Text: string,
	FakturaceprofilId: number,
	[key: string]: any,
};

export const store = listStoreBuilder({
	name: NAME,
	apiProvider,
	crumbLabel: CRUMB_LABEL_KEY,
	dropdownList: {
		VyrobekvozOzn: [],
		ProvozovnaId: [],
		ZakaznikSk: [],
		FakturaceprofilId: [] as DropdownItem[],
		PalivoPzn: [],
		LakdruhId: [],
		StrednaklOzn: [],
		Sklad: [],
		Cacelek: [],
		MkTrida: [],
		MkSkupina: [],
	},
	filter: {
		DrMatPc: null as number | null,
		DrMatSzb: null as number | null,
		FakturaceprofilId: null as number | null,
		KaroserieNr: null as string | null,
		KmStav: null as number | null,
		LakdruhOzn: null as string | null,
		LakdruhOznText: null as string | null,
		ModelRok: null as string | null,
		MotorKod: null as string | null,
		PalivoPzn: null as number | null,
		PracovnikId: null as number | null,
		PracovnikIdText: null as string | null,
		PracovnikOsobniCis: null as string | null,
		ProvozovnaId: null as number | null,
		SkladOzn: null as string | null,
		StrednaklOzn: null as number | null,
		StrednaklOznText: null as string | null,
		Typkod: null as string | null,
		TypkodText: null as string | null,
		VozidloId: null as number | null,
		VozidloIdText: null as string | null,
		VozidloSpz: null as string | null,
		VyrobekvozOzn: null as string | null,
		ZakaznikId: null as number | null,
		ZakaznikIdText: null as string | null,
		ZakaznikSk: null as number | null,
	},
	tableSetting: {},
	rowDetail: null,
	localState: {
		SnNr: null as string | null,
		MaPrp: null as boolean | null,
		CapaketVyberPrp: null as boolean | null,
		EkonomyPrp: null as boolean | null,
		CacelekNrs: [],
		MultipleSelection: [] as Row[],
		strednaklPerProvozovna: {} as { [key: number]: Set<number> },
		fakturacniProfilSchema: null as null | { data: Table },
		nakladoveStrediskoSchema: null as null | { data: Table },
		LakdruhId: null as null | number,
	},
	extraActions: {
		async [listActions.FILTER_INIT]({ state, commit, dispatch }, params) {
			return updateFilterAction(state, commit, dispatch, apiProvider.filterInit(params));
		},
	},
	extraMutations: {
		async [listMutations.PAKETOVY_MODUL_VOZIDLO_SELECT_MAPPER](state, { data }) {
			state.loadingCount += 1;
			try {
				state.filter.VozidloId = data.value;
				state.filter.VozidloSpz = data.spz;
				state.filter.KaroserieNr = data.karoserieNr;
				state.filter.VozidloIdText = data.text;
				state.filter.Typkod = data.typkod;
				state.filter.TypkodText = data.xTypkod;
				if (data.value !== null) {
					const vozidlo = await vozidloProvider.getVozidlo(data.value);
					if (vozidlo.data.ZakaznikId) {
						const zakaznik = await partnerProvider.entity(`${vozidlo.data.ZakaznikId}`);
						state.filter.ZakaznikId = vozidlo.data.ZakaznikId;
						state.filter.ZakaznikIdText = vozidlo.data.ZakaznikIdText;
						state.filter.ZakaznikSk = zakaznik.data.ZakaznikSk;
					}
					state.filter.ModelRok = vozidlo.data.ModelRok;
					state.filter.MotorKod = vozidlo.data.MotorKod;
					state.filter.PalivoPzn = vozidlo.data.PalivoPzn;
					state.filter.VyrobekvozOzn = vozidlo.data.VyrobekvozOzn;
				} else {
					state.filter.ModelRok = null;
					state.filter.MotorKod = null;
					state.filter.PalivoPzn = null;
				}
			} finally {
				state.loadingCount -= 1;
			}
		},
		[listMutations.PAKETOVY_MODUL_TYP_KOD_SELECT_MAPPER](state, { data }: { data: any }) {
			if (!data.isClear) {
				state.filter.VyrobekvozOzn = data.vyrobekvozozn;
			}
			state.filter.Typkod = data.typkod;
			state.filter.TypkodText = data.text;
		},
		[listMutations.PAKETOVY_MODUL_LAK_DRUH_SELECT_MAPPER](state, { data }) {
			state.filter.LakdruhOzn = data.value;
			state.filter.LakdruhOznText = data.text;
			state.localState.LakdruhId = data.id;
		},
		async [listMutations.PAKETOVY_MODUL_ZAKAZNIK_SELECT_MAPPER](state, { data }) {
			state.loadingCount += 1;
			try {
				state.filter.ZakaznikId = data.value;
				state.filter.ZakaznikIdText = data.text;
				if (state.filter.ZakaznikId !== null) {
					const zakaznik = await partnerProvider.entity(`${state.filter.ZakaznikId}`);
					state.filter.ZakaznikSk = zakaznik.data.ZakaznikSk;
				}
			} finally {
				state.loadingCount -= 1;
			}
		},
		[listMutations.PAKETOVY_MODUL_PRACOVNIK_SELECT_MAPPER](state, { data }) {
			state.filter.PracovnikId = data.value;
			state.filter.PracovnikIdText = data.jmeno || data.text ? `${data.text} ${data.jmeno}` : null;
			state.filter.PracovnikOsobniCis = data.osobniCis;
			state.filter.StrednaklOzn = data.strednaklOzn;
			state.filter.StrednaklOznText = data.strednaklOznText;
		},
		[listMutations.PAKETOVY_MODUL_NAKLADOVE_STREDISKO_SELECT_MAPPER](state, { data }) {
			state.filter.StrednaklOzn = data.value;
			state.filter.StrednaklOznText = data.text;
			if (!data.isClear) {
				state.filter.ProvozovnaId = data.provozovnaId;
			}
		},
		async [listMutations.PAKETOVY_MODUL_GET_FAKTURACNI_PROFIL_DROPDOWN](state) {
			state.loadingCount += 1;
			const { localState, dropdownList, filter } = state;
			try {
				if (!localState.fakturacniProfilSchema) {
					localState.fakturacniProfilSchema = await fakturacniProfilProvider.tableSchema();
				}
				const fpRows = await fakturacniProfilProvider.tableData({ ZakaznikSk: filter.ZakaznikSk });
				const fpObjects = convertToObjects({ rows: fpRows.data, columns: localState.fakturacniProfilSchema.data.columns }) as FakturacniProfil[];
				dropdownList.FakturaceprofilId = _.map(fpObjects, (p) => ({ Disabled: false, Text: p.Text, Value: p.FakturaceprofilId, Label: null }));
				filter.FakturaceprofilId = dropdownList.FakturaceprofilId[0].Value;
			} finally {
				state.loadingCount -= 1;
			}
		},
		[listMutations.PAKETOVY_MODUL_INIT_FAKTURACNI_PROFIL_ID](state) {
			state.filter.FakturaceprofilId = state.dropdownList.FakturaceprofilId[0].Value;
		},
		[listMutations.PAKETOVY_MODUL_SET_MULTIPLE_SELECTION](state, { data }) {
			state.localState.MultipleSelection = data;
		},
		async [listMutations.PAKETOVY_MODUL_CHECK_STREDNAKL](state) {
			state.loadingCount += 1;
			const { filter, localState } = state;
			try {
				if (!filter.ProvozovnaId || !filter.StrednaklOzn) return;
				if (!localState.strednaklPerProvozovna[filter.ProvozovnaId]) {
					if (!localState.nakladoveStrediskoSchema) localState.nakladoveStrediskoSchema = await nakladoveStrediskoProvider.tableSchema();
					const nsTableData = await nakladoveStrediskoProvider.tableData({ ProvozovnaId: filter.ProvozovnaId });
					const rowsColumns = { rows: nsTableData.data, columns: localState.nakladoveStrediskoSchema.data.columns };
					const nsObjects = convertToObjects(rowsColumns) as { StrednaklOzn: number, [key: string]: any }[];
					localState.strednaklPerProvozovna[filter.ProvozovnaId] = new Set(nsObjects.map((o) => o.StrednaklOzn));
				}
				if (!localState.strednaklPerProvozovna[filter.ProvozovnaId].has(filter.StrednaklOzn)) {
					filter.StrednaklOzn = null;
					filter.StrednaklOznText = null;
				}
			} finally {
				state.loadingCount -= 1;
			}
		},
	},
	extraGetters: {},
	props: {
		filter: {
			ZakazkaId: null as number | null,
			ProvozovnaId: null as number | null,
			PracovnikId: null as number | null,
			// StrednaklOzn: null as string | null,
		},
		mode: null,
		afterRowSelection: () => {},
	},
});
